import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LayoutDashboard, Monitor, Zap, Wrench, type LucideIcon } from 'lucide-react'
import { clsx } from 'clsx'
import { useAuth } from '../store/auth'
import { Button } from '../components/ui/button'
import { AuthLayout } from '../components/layout/AuthLayout'

type Choice = 'console' | 'openplace' | 'energy-os' | 'garage'

const OPTIONS: { id: Choice; title: string; description: string; icon: LucideIcon; to: string }[] = [
  { id: 'console', title: 'Rabbit Console', description: 'Configuration, data management and system administration for your organisation.', icon: LayoutDashboard, to: '/' },
  { id: 'openplace', title: 'OpenPlace', description: 'Access your dashboards and published views in a simplified interface.', icon: Monitor, to: '/open' },
  { id: 'energy-os', title: 'EnergyOS', description: "View and manage your home's energy devices.", icon: Zap, to: '/energy-os/dashboard' },
  { id: 'garage', title: 'Garage', description: 'Vehicle refrigeration repair workflow — quotes, work orders & transfers.', icon: Wrench, to: '/garage' },
]

export function ChooseApp() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [choice, setChoice] = useState<Choice | null>(null)

  const available = OPTIONS.filter(o =>
    (o.id !== 'energy-os' || user?.roles?.includes('energy-os-user')) &&
    (o.id !== 'garage' || user?.roles?.includes('garage-user')),
  )
  const selected = available.find(o => o.id === choice)

  return (
    <AuthLayout
      title="Choose your workspace"
      subtitle="Your account has access to multiple applications. Select one to continue."
    >
      <div className="mb-6 flex flex-col gap-3" role="radiogroup">
        {available.map(({ id, title, description, icon: Icon }) => {
          const active = choice === id
          return (
            <button
              key={id}
              role="radio"
              aria-checked={active}
              onClick={() => setChoice(id)}
              className={clsx(
                'flex items-start gap-4 rounded-xl border p-4 text-left transition-colors',
                active ? 'border-brand-500 bg-brand-25 ring-1 ring-brand-500' : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50',
              )}
            >
              <span className={clsx('flex h-11 w-11 shrink-0 items-center justify-center rounded-lg', active ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-500')}>
                <Icon size={22} />
              </span>
              <span className="min-w-0">
                <span className={clsx('block text-sm font-semibold', active ? 'text-brand-500' : 'text-gray-800')}>{title}</span>
                <span className="mt-0.5 block text-theme-sm text-gray-500">{description}</span>
              </span>
            </button>
          )
        })}
      </div>

      <Button size="sm" className="w-full" onClick={() => selected && navigate(selected.to)} disabled={!selected}>
        Continue
      </Button>
    </AuthLayout>
  )
}
